import { useCallback, useMemo, useRef, useState } from 'react';
import { BRAND, INDUSTRY_PRESETS } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import { copyText } from '../../telegram/telegram';
import { ErrorState, Loader } from '../../ui/feedback';
import type {
  AdminActivityPointDto,
  AdminBusinessRowDto,
  AdminEventDto,
  AdminFunnelRowDto,
  AdminOverviewDto,
  AdminRosterRowDto,
  ProGrantInput,
} from '../../api/admin';

/**
 * Админ-панель платформы — экран владельца продукта (`isAdmin`), макет v5.
 *
 * ponytail: весь текст строками, а не ключами i18n. Экран видит один русскоязычный человек,
 * узбекской версии у приватной статистики нет (как и у старого экрана, см. git-историю).
 *
 * Тема СВОЯ, тёмная премиум — таков утверждённый макет: «SaaS-админка — сам предмет», она
 * не следует светлой/тёмной теме приложения. Палитра и классы живут в одном `<style>` ниже,
 * ВСЁ под корнем `.adm`, чтобы семантические имена (`.card`, `.section`) не утекли на другие
 * экраны (там Tailwind-утилиты, а не эти классы).
 *
 * Внешних шрифтов не грузим (Google Fonts): в Mini App это лишняя сетевая зависимость и мигание
 * текста. Берём системный стек приложения; `.num` оставляет только tabular-nums для цифр.
 *
 * Все числа и ряды считает бэкенд (`adminService`). Поля, которых бэкенд не отдаёт (дельты
 * «+2», месячный режим графика, системные события), НЕ выдумываются — их просто нет на экране
 * (AGENTS.md, правило 7: тихая подстановка хуже отсутствия).
 */

// ── Вид внутренней навигации ────────────────────────────────────────────────
// Подэкраны (организации, трафик, карточка, источник, события, админы) — состояние
// компонента, а не маршруты приложения: так их не надо заводить в routes.ts и TabBar,
// а «назад» из любого всегда ведёт на главную (как в макете).
type View =
  | { name: 'main' }
  | { name: 'orgs' }
  | { name: 'traffic' }
  | { name: 'org'; id: string }
  | { name: 'source'; key: string }
  | { name: 'events' }
  | { name: 'admins' };

export function AdminScreen(props: {
  onBack: () => void;
  onOpenBusiness: (businessId: string) => void;
}): JSX.Element {
  const api = useAppApi();
  const { state, reload } = useAsync(useCallback(() => api.admin.overview(), [api]), []);
  const [view, setView] = useState<View>({ name: 'main' });
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number>(0);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 1500);
  }, []);

  const back = () => setView({ name: 'main' });

  return (
    <div className="adm">
      <style>{CSS}</style>

      {state.status === 'loading' ? <Loader /> : null}
      {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' ? (
        <Router
          view={view}
          setView={setView}
          back={back}
          data={state.data}
          reload={reload}
          onBack={props.onBack}
          onOpenBusiness={props.onOpenBusiness}
          showToast={showToast}
        />
      ) : null}

      <div className={`toast${toast ? ' on' : ''}`}>{toast}</div>
    </div>
  );
}

interface Ctx {
  view: View;
  setView: (v: View) => void;
  back: () => void;
  data: AdminOverviewDto;
  reload: () => void;
  onBack: () => void;
  onOpenBusiness: (id: string) => void;
  showToast: (msg: string) => void;
}

function Router(ctx: Ctx): JSX.Element {
  switch (ctx.view.name) {
    case 'main':
      return <MainView ctx={ctx} />;
    case 'orgs':
      return <OrgsView ctx={ctx} />;
    case 'traffic':
      return <TrafficView ctx={ctx} />;
    case 'org':
      return <OrgView ctx={ctx} id={ctx.view.id} />;
    case 'source':
      return <SourceView ctx={ctx} srcKey={ctx.view.key} />;
    case 'events':
      return <EventsView ctx={ctx} />;
    case 'admins':
      return <AdminsView ctx={ctx} />;
  }
}

// ── Главный экран ─────────────────────────────────────────────────────────────

function MainView({ ctx }: { ctx: Ctx }): JSX.Element {
  const { totals, businesses, funnel, activity, events } = ctx.data;
  const sources = useMemo(() => buildSources(funnel), [funnel]);
  const planCounts = `${totals.free} Free · ${totals.trial} Trial · ${totals.pro} Pro`;

  return (
    <section className="screen active">
      <div className="tgbar">
        <button className="round" onClick={ctx.onBack} aria-label="Закрыть">
          ✕
        </button>
        <div className="title">
          <b>{BRAND.name}</b>
          <span>Админ-панель</span>
        </div>
        <span className="round" aria-hidden="true" style={{ visibility: 'hidden' }} />
      </div>

      <div className="body">
        <div className="toprow">
          <span className="status">
            <i className="dot" /> Система работает
          </span>
        </div>

        <div className="lead">
          <div className="lead-ico">
            <Svg d='<path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="6" rx="1"/><rect x="12" y="8" width="3" height="9" rx="1"/><rect x="17" y="5" width="3" height="12" rx="1"/>' />
          </div>
          <div className="leadtx">
            <h1>Статистика платформы</h1>
            <p>Общая картина по всем организациям</p>
          </div>
        </div>

        <Metrics totals={totals} activity={activity} />

        <ActivityChart activity={activity} />

        <SourcesCard sources={sources} onOpen={() => ctx.setView({ name: 'traffic' })} onSource={(k) => ctx.setView({ name: 'source', key: k })} />

        <button className="card entry" onClick={() => ctx.setView({ name: 'orgs' })}>
          <span className="ei">
            <Svg d='<path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/>' size={20} />
          </span>
          <span className="em">
            <b>Организации</b>
            <span>{businesses.length} всего · {planCounts}</span>
          </span>
          <span className="ec">
            <Svg d='<path d="m9 18 6-6-6-6"/>' size={18} sw={2.2} />
          </span>
        </button>

        <button className="card entry" onClick={() => ctx.setView({ name: 'admins' })}>
          <span className="ei">
            <Svg d='<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>' size={20} />
          </span>
          <span className="em">
            <b>Администраторы</b>
            <span>Доступ к панели и бесплатный Pro</span>
          </span>
          <span className="ec">
            <Svg d='<path d="m9 18 6-6-6-6"/>' size={18} sw={2.2} />
          </span>
        </button>

        <div className="card section">
          <div className="head">
            <span className="t">
              <span className="hico">
                <Svg d='<path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="9"/>' size={17} />
              </span>
              Последние события
            </span>
            <button className="link-r" onClick={() => ctx.setView({ name: 'events' })}>
              Все события <Svg d='<path d="m9 18 6-6-6-6"/>' size={13} sw={2.2} />
            </button>
          </div>
          <div className="events">
            {events.length ? (
              events.slice(0, 2).map((e, i) => <EventRow key={i} e={e} />)
            ) : (
              <div className="empty">Событий пока нет</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Метрики ───────────────────────────────────────────────────────────────────

function Metrics({ totals, activity }: { totals: AdminOverviewDto['totals']; activity: AdminActivityPointDto[] }): JSX.Element {
  // Спарклайн — только где бэкенд даёт дневной ряд (организации, записи). У «клиентов»
  // и «неявок» дневного ряда нет — плитка просто без спарка, а не с выдуманной линией.
  const regSpark = activity.map((a) => a.registrations);
  const apptSpark = activity.map((a) => a.appointments);
  const cards: { icon: string; val: number; lbl: string; spark: number[] | null; col: string }[] = [
    { icon: ICON.org, val: totals.businesses, lbl: 'Организаций', spark: regSpark, col: 'var(--lime)' },
    { icon: ICON.users, val: totals.clients, lbl: totals.clients === 1 ? 'Клиент' : 'Клиентов', spark: null, col: 'var(--teal)' },
    { icon: ICON.cal, val: totals.appointments, lbl: 'Записей', spark: apptSpark, col: 'var(--lime)' },
    { icon: ICON.check, val: totals.noShow, lbl: 'Неявок', spark: null, col: 'var(--grey)' },
  ];
  return (
    <div className="grid2">
      {cards.map((c) => (
        <div key={c.lbl} className="card metric">
          <div className="mico">
            <Svg d={c.icon} size={15} />
          </div>
          <div className="mr">
            <div className="mtop">
              <span className="val num">{c.val}</span>
            </div>
            <div className="lbl">{c.lbl}</div>
          </div>
          {c.spark && c.spark.some((v) => v > 0) ? (
            <div className="sparkbox" dangerouslySetInnerHTML={{ __html: sparkSvg(c.spark, c.col) }} />
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ── График активности ───────────────────────────────────────────────────────

type Period = '7' | '30';

function ActivityChart({ activity }: { activity: AdminActivityPointDto[] }): JSX.Element {
  const [period, setPeriod] = useState<Period>('30');
  const [hover, setHover] = useState<number | null>(null);
  const points = period === '7' ? activity.slice(-7) : activity;

  const W = 340, H = 108, L = 10, R = 10, T = 8, B = 20;
  const n = Math.max(points.length, 1);
  const appts = points.map((p) => p.appointments);
  const regs = points.map((p) => p.registrations);
  const pro = points.map((p) => p.pro);
  const yMax = Math.max(2, Math.ceil(Math.max(...appts, ...regs, ...pro, 4) / 2) * 2);
  const x = (i: number): number => (n === 1 ? L : L + (i * (W - L - R)) / (n - 1));
  const y = (v: number): number => T + (1 - v / yMax) * (H - T - B);

  const lineA = smooth(appts.map((v, i) => [x(i), y(v)]));
  const lineR = smooth(regs.map((v, i) => [x(i), y(v)]));
  const lineP = smooth(pro.map((v, i) => [x(i), y(v)]));
  const areaA = `${lineA} L ${x(n - 1)},${H - B} L ${x(0)},${H - B} Z`;

  const grid: number[] = [0, yMax / 2, yMax];
  // Подписи оси X: до пяти равномерных дней.
  const tickStep = Math.max(1, Math.ceil(n / 5));
  const ticks: number[] = [];
  for (let i = 0; i < n; i += tickStep) ticks.push(i);
  if (ticks[ticks.length - 1] !== n - 1) ticks.push(n - 1);

  const onMove = (e: React.PointerEvent<SVGRectElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.max(0, Math.min(n - 1, Math.round((cx - L) / ((W - L - R) / Math.max(n - 1, 1)))));
    setHover(i);
  };

  const hi = hover ?? n - 1;
  const hp = points[hi];

  return (
    <div className="card section">
      <div className="head">
        <span className="t">
          <span className="hico">
            <Svg d='<path d="M3 12h4l3 8 4-16 3 8h4"/>' size={17} />
          </span>
          Динамика активности
        </span>
        <div className="seg">
          <button className={period === '7' ? 'on' : ''} onClick={() => setPeriod('7')}>
            7 дн.
          </button>
          <button className={period === '30' ? 'on' : ''} onClick={() => setPeriod('30')}>
            30 дн.
          </button>
        </div>
      </div>

      <div className="chartwrap">
        <svg viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <linearGradient id="admAreaA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--lime)" stopOpacity=".2" />
              <stop offset="1" stopColor="var(--lime)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {grid.map((g, i) => (
            <g key={i}>
              <line x1={L} y1={y(g)} x2={W - R} y2={y(g)} stroke="var(--border)" strokeWidth={1} strokeDasharray={g === 0 ? undefined : '2 4'} />
              <text x={L} y={y(g) - 4} fill="var(--muted)" fontSize="8.5">
                {g}
              </text>
            </g>
          ))}
          {ticks.map((i) => (
            <text key={i} x={x(i)} y={H - 6} fill="var(--muted)" fontSize="9" textAnchor="middle">
              {dayNum(points[i]?.day)}
            </text>
          ))}
          <path d={areaA} fill="url(#admAreaA)" />
          <path d={lineP} fill="none" stroke="var(--gold)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
          <path d={lineR} fill="none" stroke="var(--teal)" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
          <path d={lineA} fill="none" stroke="var(--lime)" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
          {hp ? (
            <g>
              <line x1={x(hi)} x2={x(hi)} y1={T} y2={H - B} stroke="var(--border)" strokeWidth={1} />
              <circle cx={x(hi)} cy={y(hp.appointments)} r={3.4} fill="var(--lime)" stroke="var(--bg)" strokeWidth={2} />
              <circle cx={x(hi)} cy={y(hp.registrations)} r={3.4} fill="var(--teal)" stroke="var(--bg)" strokeWidth={2} />
              <circle cx={x(hi)} cy={y(hp.pro)} r={3.4} fill="var(--gold)" stroke="var(--bg)" strokeWidth={2} />
            </g>
          ) : null}
          <rect x={0} y={0} width={W} height={H} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
        </svg>
        {hp ? (
          <div
            className="tip"
            style={{ left: `${Math.min(Math.max((x(hi) / W) * 100, 18), 82)}%`, top: `${(y(Math.max(hp.appointments, hp.registrations, hp.pro)) / H) * 100 - 6}%` }}
          >
            <div className="d">{dayFull(hp.day)}</div>
            <div className="tr">
              <span>
                <i style={{ background: 'var(--lime)' }} />
                Записи
              </span>
              <b className="num">{hp.appointments}</b>
            </div>
            <div className="tr">
              <span>
                <i style={{ background: 'var(--teal)' }} />
                Регистрации
              </span>
              <b className="num">{hp.registrations}</b>
            </div>
            <div className="tr">
              <span>
                <i style={{ background: 'var(--gold)' }} />
                Pro
              </span>
              <b className="num">{hp.pro}</b>
            </div>
          </div>
        ) : null}
      </div>

      <div className="legend">
        <span>
          <i style={{ background: 'var(--lime)' }} />
          Записи
        </span>
        <span>
          <i style={{ background: 'var(--teal)' }} />
          Регистрации
        </span>
        <span>
          <i style={{ background: 'var(--gold)' }} />
          Pro-подписки
        </span>
      </div>
    </div>
  );
}

// ── Источники трафика (карточка на главной) ──────────────────────────────────

interface Source {
  key: string;
  label: string;
  name: string;
  pct: number;
  color: string;
  row: AdminFunnelRowDto;
}

const SRC_COLORS = ['var(--lime)', 'var(--teal)', 'var(--gold)', 'var(--grey)'];

function buildSources(funnel: readonly AdminFunnelRowDto[]): Source[] {
  const total = funnel.reduce((s, r) => s + r.started, 0) || 1;
  return funnel.map((r, i) => {
    const label = r.source.replace(/^src_/, '');
    return {
      key: r.source,
      label,
      name: label || '(без метки)',
      pct: Math.round((r.started / total) * 100),
      color: SRC_COLORS[i % SRC_COLORS.length] ?? 'var(--grey)',
      row: r,
    };
  });
}

function SourcesCard({ sources, onOpen, onSource }: { sources: Source[]; onOpen: () => void; onSource: (k: string) => void }): JSX.Element {
  return (
    <div className="card section">
      <div className="head">
        <span className="t">
          <span className="hico">
            <Svg d='<path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/><line x1="8" y1="12" x2="16" y2="12"/>' size={17} />
          </span>
          Источники трафика
        </span>
        <span className="link-r">
          {sources.length} источник{plural(sources.length, '', 'а', 'ов')}
        </span>
      </div>
      {sources.length ? (
        <div className="donutrow">
          <div className="donut" dangerouslySetInnerHTML={{ __html: donutSvg(sources) }} />
          <div className="srclist">
            {sources.map((s) => (
              <button key={s.key} className="srcrow" onClick={() => onSource(s.key)}>
                <div className="l1">
                  <span className="nm">
                    <i style={{ background: s.color }} />
                    {s.name}
                  </span>
                  <span className="pc num">{s.pct}%</span>
                </div>
                <div className="track">
                  <span style={{ width: `${s.pct}%`, background: s.color }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty">Меток источника пока нет</div>
      )}
      <button className="cardbtn" onClick={onOpen}>
        <span className="hico">
          <Svg d='<path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/><line x1="8" y1="12" x2="16" y2="12"/>' size={16} />
        </span>
        Ссылки-метки и подробнее
      </button>
    </div>
  );
}

// ── Организации (вкладка) ─────────────────────────────────────────────────────

type PlanFilter = 'all' | 'free' | 'trial' | 'pro';

/** Карта enum-отрасли → русское название (из пресетов, не хардкод). */
const TYPE_NAME = new Map<string, string>(INDUSTRY_PRESETS.map((p) => [p.type, p.nameRu]));
const typeName = (t: string): string => TYPE_NAME.get(t) ?? t;

function OrgsView({ ctx }: { ctx: Ctx }): JSX.Element {
  const [plan, setPlan] = useState<PlanFilter>('all');
  const [ind, setInd] = useState<string>('all');
  const [query, setQuery] = useState('');
  const list = ctx.data.businesses.filter(
    (b) =>
      (plan === 'all' || b.plan === plan) &&
      (ind === 'all' || (ind === '__other' ? !TYPE_NAME.has(b.type) : b.type === ind)) &&
      b.name.toLowerCase().includes(query.toLowerCase()),
  );

  const indChips: [string, string][] = [['all', 'Все отрасли'], ...INDUSTRY_PRESETS.map((p): [string, string] => [p.type, p.nameRu]), ['__other', 'Остальные']];

  return (
    <section className="screen active">
      <SubHead title="Организации" sub={`${ctx.data.businesses.length} организаций`} onBack={ctx.back} />
      <div className="body">
        <div className="search">
          <Svg d='<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>' size={16} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Поиск по названию…" autoComplete="off" />
        </div>
        <div className="filters">
          {(['all', 'free', 'trial', 'pro'] as PlanFilter[]).map((p) => (
            <button key={p} className={`chip${plan === p ? ' on' : ''}`} onClick={() => setPlan(p)}>
              {p === 'all' ? 'Все' : PLAN_LABEL[p]}
            </button>
          ))}
        </div>
        <div className="filters">
          {indChips.map(([v, l]) => (
            <button key={v} className={`chip${ind === v ? ' on' : ''}`} onClick={() => setInd(v)}>
              {l}
            </button>
          ))}
        </div>
        <div className="orglist">
          {list.length ? (
            list.map((b) => <OrgCard key={b.id} b={b} onClick={() => ctx.setView({ name: 'org', id: b.id })} />)
          ) : (
            <div className="empty">Ничего не найдено</div>
          )}
        </div>
      </div>
    </section>
  );
}

const PLAN_LABEL: Record<AdminBusinessRowDto['plan'], string> = { free: 'Free', trial: 'Trial', pro: 'Pro' };

function OrgCard({ b, onClick }: { b: AdminBusinessRowDto; onClick: () => void }): JSX.Element {
  return (
    <button className="card orgcard" onClick={onClick}>
      <span className="ava">{b.name[0] ?? '?'}</span>
      <span className="orgmid">
        <span className="n1">
          <b>{b.name}</b>
          <span className={`badge b-${b.plan}`}>{PLAN_LABEL[b.plan]}</span>
        </span>
        <span className="otype">
          <span className="typ">{typeName(b.type)}</span>
          {b.city ? ` · ${b.city}` : ''}
        </span>
        <span className="n2">
          <span>
            <Svg d='<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>' size={13} sw={1.8} /> {b.clients} клиент{plural(b.clients, '', 'а', 'ов')}
          </span>
          <span>
            <Svg d='<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/>' size={13} sw={1.8} /> {b.appointments} {plural(b.appointments, 'запись', 'записи', 'записей')}
          </span>
        </span>
      </span>
      <span className="orgright">
        <span className="st">
          <i style={{ background: b.isPublished ? 'var(--lime)' : 'var(--grey)' }} />
          {b.isPublished ? 'В каталоге' : 'Черновик'}
        </span>
        <span className="dt">{fmtShort(b.lastActivityAt)}</span>
      </span>
    </button>
  );
}

// ── Карточка организации (+ выдача Pro) ──────────────────────────────────────

function OrgView({ ctx, id }: { ctx: Ctx; id: string }): JSX.Element {
  const b = ctx.data.businesses.find((x) => x.id === id);
  if (!b) {
    return (
      <section className="screen active">
        <SubHead title="Организация" sub="Карточка организации" onBack={ctx.back} />
        <div className="body">
          <div className="empty">Организация не найдена</div>
        </div>
      </section>
    );
  }
  const hist = ctx.data.events.filter((e) => e.org === b.name);

  return (
    <section className="screen active">
      <SubHead title={b.name} sub="Карточка организации" onBack={ctx.back} />
      <div className="body">
        <div className="orgcard card" style={{ cursor: 'default' }}>
          <span className="ava" style={{ width: 52, height: 52, flexBasis: 52, fontSize: 21 }}>
            {b.name[0] ?? '?'}
          </span>
          <span className="orgmid">
            <span className="n1">
              <b style={{ fontSize: 17 }}>{b.name}</b>
              <span className={`badge b-${b.plan}`}>{PLAN_LABEL[b.plan]}</span>
            </span>
            <span className="otype">
              <span className="typ">{typeName(b.type)}</span>
              {b.city ? ` · ${b.city}` : ''}
            </span>
          </span>
        </div>

        <div className="kpis">
          <div className="kpi">
            <div className="v num">{b.clients}</div>
            <div className="l">Клиентов</div>
          </div>
          <div className="kpi">
            <div className="v num">{b.appointments}</div>
            <div className="l">Записей</div>
          </div>
          <div className="kpi">
            <div className="v num">{b.noShow}</div>
            <div className="l">Неявок</div>
          </div>
        </div>

        <ProGrant ctx={ctx} b={b} />

        <div className="card section" style={{ padding: '4px 14px' }}>
          <div className="rows">
            <div className="kv">
              <span className="k">Вид деятельности</span>
              <span className="v">{typeName(b.type)}</span>
            </div>
            <div className="kv">
              <span className="k">Тариф</span>
              <span className="v">{PLAN_LABEL[b.plan]}</span>
            </div>
            <div className="kv">
              <span className="k">В каталоге</span>
              <span className="v" style={{ color: b.isPublished ? 'var(--lime)' : 'var(--muted)' }}>
                {b.isPublished ? 'да' : 'черновик'}
              </span>
            </div>
            <div className="kv">
              <span className="k">Последняя активность</span>
              <span className="v">{b.lastActivityAt ? fmtShort(b.lastActivityAt) : 'записей нет'}</span>
            </div>
          </div>
        </div>

        <button className="cardbtn" onClick={() => ctx.onOpenBusiness(b.id)}>
          <span className="hico">
            <Svg d='<path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>' size={16} />
          </span>
          Подробная статистика
        </button>

        <div className="sectitle">История событий</div>
        <div className="card section" style={{ padding: '6px 14px' }}>
          <div className="events">
            {hist.length ? hist.map((e, i) => <EventRow key={i} e={e} />) : <div className="empty">Событий пока нет</div>}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Ручная выдача Pro: кнопки продления/бессрочно/снять. Бэкенд пишет `pro_until` как оплата. */
function ProGrant({ ctx, b }: { ctx: Ctx; b: AdminBusinessRowDto }): JSX.Element {
  const api = useAppApi();
  const [busy, setBusy] = useState(false);

  const grant = async (g: ProGrantInput, label: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.admin.grantPro(b.id, g);
      ctx.showToast(r.isPro && r.proUntil ? `Pro до ${fmtShort(r.proUntil)}` : 'Pro снят');
      ctx.reload(); // обзор пересчитает план организации — карточка перерисуется из свежих данных.
    } catch {
      ctx.showToast('Не получилось');
    } finally {
      setBusy(false);
    }
    void label;
  };

  return (
    <div className="card section">
      <div className="head">
        <span className="t">
          <span className="hico">
            <Svg d='<path d="M12 2 4 6v6c0 5 3.4 7.7 8 10 4.6-2.3 8-5 8-10V6z"/>' size={17} />
          </span>
          Pro-доступ
        </span>
        <span className={`badge b-${b.plan}`}>{PLAN_LABEL[b.plan]}</span>
      </div>
      <div className="grantrow">
        <button disabled={busy} onClick={() => grant({ months: 1 }, '+1')}>
          +1 мес
        </button>
        <button disabled={busy} onClick={() => grant({ months: 2 }, '+2')}>
          +2 мес
        </button>
        <button disabled={busy} onClick={() => grant({ months: 3 }, '+3')}>
          +3 мес
        </button>
        <button disabled={busy} onClick={() => grant({ forever: true }, 'forever')}>
          Бессрочно
        </button>
        <button disabled={busy} className="danger" onClick={() => grant({ revoke: true }, 'revoke')}>
          Снять
        </button>
      </div>
    </div>
  );
}

// ── Источник (аналитика метки) ───────────────────────────────────────────────

function SourceView({ ctx, srcKey }: { ctx: Ctx; srcKey: string }): JSX.Element {
  const r = ctx.data.funnel.find((x) => x.source === srcKey);
  const name = srcKey.replace(/^src_/, '') || '(без метки)';
  if (!r) {
    return (
      <section className="screen active">
        <SubHead title={name} sub="Аналитика источника" onBack={ctx.back} />
        <div className="body">
          <div className="empty">Источник не найден</div>
        </div>
      </section>
    );
  }
  // Шаги воронки — из реальных полей funnel. «Запусков приложения» отдельно бэкенд не даёт,
  // этот шаг не показываем, а не рисуем равным переходам.
  const steps: { icon: string; l: string; v: number }[] = [
    { icon: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>', l: 'Переходов по ссылке', v: r.started },
    { icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>', l: 'Владельцев пришло', v: r.owners },
    { icon: '<path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/>', l: 'Организаций создано', v: r.businesses },
    { icon: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><path d="m9 16 2 2 4-4"/>', l: 'С записями', v: r.withBooking },
  ];
  const conv = (a: number, b: number): number => (a ? Math.round((b / a) * 100) : 0);

  return (
    <section className="screen active">
      <SubHead title={name} sub="Аналитика источника" onBack={ctx.back} />
      <div className="body">
        <div className="funnel">
          {steps.map((st, i) => (
            <div key={i}>
              <div className="fstep">
                <div className="fi">
                  <Svg d={st.icon} size={18} sw={1.9} />
                </div>
                <div className="fm">
                  <div className="fl">{st.l}</div>
                  <div className="fv num">{st.v}</div>
                </div>
              </div>
              {i < steps.length - 1 ? (
                <div className="conv">
                  ↓ конверсия <b>{conv(st.v, steps[i + 1]!.v)}%</b>
                </div>
              ) : null}
            </div>
          ))}
        </div>
        <div className="sectitle">Параметры источника</div>
        <div className="card section" style={{ padding: '4px 14px' }}>
          <div className="rows">
            <div className="kv">
              <span className="k">Метка ссылки</span>
              <span className="v" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {srcKey}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Трафик + генератор ссылок-меток ───────────────────────────────────────────

const LABEL_RE = /^[a-z0-9_]{1,40}$/;
const LABELS_KEY = 'app.adminSrcLabels';

function readLabels(): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(LABELS_KEY) ?? '[]');
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
function writeLabels(labels: string[]): void {
  try {
    localStorage.setItem(LABELS_KEY, JSON.stringify(labels));
  } catch {
    // Приватный режим: список не запомнится, но ссылка живёт в самой метке `src_…`.
  }
}
const linkFor = (label: string): string => `https://t.me/${BRAND.botUsername}?start=src_${label}`;

function TrafficView({ ctx }: { ctx: Ctx }): JSX.Element {
  const sources = useMemo(() => buildSources(ctx.data.funnel), [ctx.data.funnel]);
  const startedBy = useMemo(() => new Map(ctx.data.funnel.map((r) => [r.source, r.started])), [ctx.data.funnel]);
  const [labels, setLabels] = useState<string[]>(readLabels);
  const [input, setInput] = useState('');

  const clean = input.trim().toLowerCase();
  const exists = sources.some((s) => s.label === clean) || labels.includes(clean);
  const err = clean && !LABEL_RE.test(clean) ? 'Можно латиницу в нижнем регистре, цифры и _' : exists ? 'Такая метка уже есть' : '';
  const valid = LABEL_RE.test(clean) && !exists;

  const create = async (): Promise<void> => {
    if (!valid) return;
    const next = [clean, ...labels];
    setLabels(next);
    writeLabels(next);
    setInput('');
    try {
      await copyText(linkFor(clean));
      ctx.showToast('Скопировано');
    } catch {
      ctx.showToast('Ссылка создана');
    }
  };

  const copy = async (label: string): Promise<void> => {
    try {
      await copyText(linkFor(label));
      ctx.showToast('Скопировано');
    } catch {
      /* копирование недоступно — ссылка видна и выделяется вручную */
    }
  };

  // Метки = источники из воронки (с реальными переходами) + созданные вручную, которых ещё нет.
  const seeded = sources.map((s) => ({ label: s.label, clicks: s.row.started, seeded: true }));
  const custom = labels
    .filter((l) => !sources.some((s) => s.label === l))
    .map((l) => ({ label: l, clicks: startedBy.get(`src_${l}`) ?? 0, seeded: false }));
  const all = [...seeded, ...custom];

  return (
    <section className="screen active">
      <SubHead title="Источники трафика" sub="Откуда приходят и ссылки-метки" onBack={ctx.back} />
      <div className="body">
        <div className="sectitle">Разбивка по источникам</div>
        <div className="card section" style={{ padding: '6px 14px' }}>
          <div className="rows">
            {sources.length ? (
              sources.map((s) => (
                <button key={s.key} className="kv" style={{ cursor: 'pointer' }} onClick={() => ctx.setView({ name: 'source', key: s.key })}>
                  <span className="k">
                    <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: s.color, marginRight: 8 }} />
                    {s.name}
                  </span>
                  <span className="v num">
                    {s.pct}% · {s.row.started} перех.
                  </span>
                </button>
              ))
            ) : (
              <div className="empty">Данных пока нет</div>
            )}
          </div>
        </div>

        <div className="sectitle">Ссылки-метки</div>
        <div className="card gen">
          <div className="glab">Впиши своё название в конце — ссылку соберём сами. По ней придёт отчёт о трафике.</div>
          <input className="geninput2" value={input} onChange={(e) => setInput(e.target.value.toLowerCase())} placeholder="instagram_bio" autoComplete="off" spellCheck={false} onKeyDown={(e) => e.key === 'Enter' && create()} />
          <div className="genprev">
            t.me/{BRAND.botUsername}?start=src_<b>{clean || 'instagram_bio'}</b>
          </div>
          <div className="generr">{err}</div>
          <button className="genbtn" disabled={!valid} onClick={create}>
            Создать и скопировать ссылку
          </button>
        </div>

        <div className="lbllist">
          {all.map((l) => (
            <div key={l.label} className="lblrow">
              <div className="lb">
                <b>
                  src_{l.label}
                  {l.seeded ? null : <span className="badge b-pro">новая</span>}
                </b>
                <div className="ln">{linkFor(l.label)}</div>
              </div>
              <div className="cnt">
                <b className="num">{l.clicks}</b>
                <span>переходов</span>
              </div>
              <button className="copy" onClick={() => copy(l.label)} aria-label="Копировать">
                <Svg d='<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>' size={15} sw={1.8} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Журнал событий ────────────────────────────────────────────────────────────

type EvFilter = 'all' | 'business' | 'appointment' | 'pro';

function EventsView({ ctx }: { ctx: Ctx }): JSX.Element {
  const [f, setF] = useState<EvFilter>('all');
  const list = f === 'all' ? ctx.data.events : ctx.data.events.filter((e) => e.kind === f);
  const chips: [EvFilter, string][] = [
    ['all', 'Все'],
    ['business', 'Регистрации'],
    ['appointment', 'Записи'],
    ['pro', 'Pro'],
  ];
  return (
    <section className="screen active">
      <SubHead title="Журнал событий" sub="Все события платформы" onBack={ctx.back} />
      <div className="body">
        <div className="filters">
          {chips.map(([v, l]) => (
            <button key={v} className={`chip${f === v ? ' on' : ''}`} onClick={() => setF(v)}>
              {l}
            </button>
          ))}
        </div>
        <div className="card section" style={{ padding: '6px 14px' }}>
          <div className="events">
            {list.length ? list.map((e, i) => <EventRow key={i} e={e} />) : <div className="empty">Событий нет</div>}
          </div>
        </div>
      </div>
    </section>
  );
}

const EV_META: Record<AdminEventDto['kind'], { icon: string; title: string }> = {
  business: { icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>', title: 'Новая организация' },
  appointment: { icon: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="m9 16 2 2 4-4"/>', title: 'Создана запись' },
  pro: { icon: '<polyline points="13 2 13 9 20 9"/><path d="M4 13v-2a2 2 0 0 1 2-2h5"/><path d="m14 19 3-3 3 3"/><path d="M17 16v6"/>', title: 'Переход на Pro' },
};

function EventRow({ e }: { e: AdminEventDto }): JSX.Element {
  const m = EV_META[e.kind];
  return (
    <div className="ev">
      <div className="evico">
        <Svg d={m.icon} size={16} sw={1.9} />
      </div>
      <div className="evtx">
        <b>{m.title}</b>
        <span>
          {e.org} · {fmtEvent(e.at)}
        </span>
      </div>
    </div>
  );
}

// ── Администраторы платформы ──────────────────────────────────────────────────

const TG_ID_RE = /^\d{1,20}$/;

function AdminsView({ ctx }: { ctx: Ctx }): JSX.Element {
  const api = useAppApi();
  const { state, reload } = useAsync(useCallback(() => api.admin.listAdmins(), [api]), []);
  const [tgId, setTgId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const validId = TG_ID_RE.test(tgId.trim());

  const add = async (): Promise<void> => {
    if (!validId || busy) return;
    setBusy(true);
    try {
      await api.admin.addAdmin(tgId.trim(), note.trim());
      setTgId('');
      setNote('');
      ctx.showToast('Админ добавлен');
      reload();
    } catch {
      ctx.showToast('Не получилось');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: AdminRosterRowDto): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await api.admin.removeAdmin(row.tgId);
      ctx.showToast('Админ снят');
      reload();
    } catch {
      ctx.showToast('Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="screen active">
      <SubHead title="Администраторы" sub="Доступ к панели и бесплатный Pro" onBack={ctx.back} />
      <div className="body">
        <div className="card gen">
          <div className="glab">Добавь по Telegram ID. Админ получает доступ к этой панели, а его организации — бесплатный Pro.</div>
          <input className="geninput2" value={tgId} onChange={(e) => setTgId(e.target.value.replace(/\D/g, ''))} placeholder="Telegram ID (цифры)" inputMode="numeric" autoComplete="off" />
          <input className="geninput2" style={{ marginTop: 10, fontFamily: 'inherit' }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Пометка: кто это (необязательно)" autoComplete="off" />
          <button className="genbtn" disabled={!validId || busy} onClick={add} style={{ marginTop: 12 }}>
            Добавить админа
          </button>
        </div>

        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}
        {state.status === 'ready' ? (
          <div className="lbllist">
            {state.data.map((row) => (
              <div key={row.tgId} className="lblrow">
                <div className="lb">
                  <b>
                    {row.note || 'Без пометки'}
                    {row.isOwner ? <span className="badge b-pro">владелец</span> : null}
                  </b>
                  <div className="ln">ID {row.tgId}</div>
                </div>
                {row.isOwner ? (
                  <span className="cnt">
                    <span>бутстрап</span>
                  </span>
                ) : (
                  <button className="copy danger" disabled={busy} onClick={() => remove(row)} aria-label="Снять">
                    <Svg d='<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>' size={15} sw={1.8} />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ── Общие мелочи ──────────────────────────────────────────────────────────────

function SubHead({ title, sub, onBack }: { title: string; sub: string; onBack: () => void }): JSX.Element {
  return (
    <div className="dhead">
      <button className="round" onClick={onBack} aria-label="Назад">
        ‹
      </button>
      <div>
        <b>{title}</b>
        <div className="sub">{sub}</div>
      </div>
    </div>
  );
}

function Svg({ d, size = 18, sw = 2 }: { d: string; size?: number; sw?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}

const ICON = {
  org: '<path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  cal: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  check: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>',
};

/** Catmull-Rom → Безье: мягкая линия без «гуляния». */
function smooth(p: [number, number][]): string {
  if (p.length < 2) return p.length ? `M ${p[0]![0]},${p[0]![1]}` : '';
  let d = `M ${p[0]![0]},${p[0]![1]}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p1 = p[i]!;
    const p2 = p[i + 1]!;
    const p0 = p[i - 1] ?? p1;
    const p3 = p[i + 2] ?? p2;
    d += ` C ${p1[0] + (p2[0] - p0[0]) / 6},${p1[1] + (p2[1] - p0[1]) / 6} ${p2[0] - (p3[0] - p1[0]) / 6},${p2[1] - (p3[1] - p1[1]) / 6} ${p2[0]},${p2[1]}`;
  }
  return d;
}

function sparkSvg(vals: number[], color: string): string {
  const w = 120, h = 28, pad = 3;
  const max = Math.max(...vals), min = Math.min(...vals), flat = max === min;
  const pts: [number, number][] = vals.map((v, i) => [pad + (i * (w - 2 * pad)) / Math.max(vals.length - 1, 1), h - pad - (flat ? 0.5 : (v - min) / (max - min)) * (h - 2 * pad)]);
  const line = smooth(pts);
  const area = `${line} L ${w - pad},${h} L ${pad},${h} Z`;
  const gid = 'sg' + Math.random().toString(36).slice(2, 7);
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none"><defs><linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${color}" stop-opacity=".26"/><stop offset="1" stop-color="${color}" stop-opacity="0"/></linearGradient></defs><path d="${area}" fill="url(#${gid})"/><path d="${line}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round"/></svg>`;
}

function donutSvg(sources: Source[]): string {
  const R = 43, C = 2 * Math.PI * R;
  let off = 0;
  const segs = sources
    .map((s) => {
      const len = (C * s.pct) / 100;
      const seg = `<circle cx="48" cy="48" r="${R}" fill="none" stroke="${s.color}" stroke-width="10" stroke-dasharray="${len} ${C - len}" stroke-dashoffset="${-off}" transform="rotate(-90 48 48)"/>`;
      off += len;
      return seg;
    })
    .join('');
  return `<svg viewBox="0 0 96 96" width="96" height="96"><circle cx="48" cy="48" r="43" fill="none" stroke="var(--card-2)" stroke-width="10"/>${segs}</svg><div class="center"><b class="num">${sources[0]?.pct ?? 0}%</b></div>`;
}

/** 1→form1, 2–4→form2, иначе→form5 (русская множественность). */
function plural(n: number, f1: string, f24: string, f5: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return f1;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return f24;
  return f5;
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
/** День месяца из 'YYYY-MM-DD'. */
function dayNum(day: string | undefined): string {
  return day ? String(+day.slice(8, 10)) : '';
}
/** 'YYYY-MM-DD' → «27 окт». */
function dayFull(day: string): string {
  return `${+day.slice(8, 10)} ${MONTHS[+day.slice(5, 7) - 1] ?? ''}`;
}
/** ISO → «27 окт» (последняя активность). */
function fmtShort(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
/** ISO → «27 окт, 14:37» (лента событий). */
function fmtEvent(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${hh}:${mm}`;
}

// ── Стили (тёмная тема v5, всё под .adm) ─────────────────────────────────────
const CSS = `
.adm{
  --bg:#0B0D0E; --card:#17191A; --card-2:#202223; --border:#2B2D2E;
  --text:#F1F5F4; --muted:#929797; --lime:#B6F542; --teal:#35C6C9; --grey:#5A5F5F; --gold:#E6B450;
  --lime-ink:#0B0D0E; --r-card:18px; --r-inner:12px;
  color-scheme:dark; background:var(--bg); color:var(--text); min-height:100%;
  line-height:1.4; -webkit-font-smoothing:antialiased;
}
.adm *{ box-sizing:border-box; }
.adm .num{ font-variant-numeric:tabular-nums; }
.adm .screen{ display:flex; flex-direction:column; min-height:100%; }
.adm .screen.active{ animation:admfade .2s ease; }
@keyframes admfade{ from{opacity:0;transform:translateY(4px);} to{opacity:1;transform:none;} }
@media (prefers-reduced-motion:reduce){ .adm .screen.active{ animation:none; } }

.adm .tgbar,.adm .dhead{
  position:sticky; top:0; z-index:20; display:flex; align-items:center; gap:10px;
  padding:calc(env(safe-area-inset-top,0px) + 11px) 14px 11px;
  background:rgba(11,13,14,.82); backdrop-filter:blur(14px); border-bottom:1px solid rgba(43,45,46,.6);
}
.adm .tgbar .title{ flex:1; text-align:center; }
.adm .tgbar .title b{ display:block; font-size:15px; font-weight:600; }
.adm .tgbar .title span{ display:block; font-size:11px; color:var(--muted); margin-top:1px; }
.adm .round{ width:34px; height:34px; flex:0 0 34px; display:grid; place-items:center; border-radius:50%; background:var(--card-2); color:var(--text); border:1px solid var(--border); cursor:pointer; font-size:17px; padding:0; font-family:inherit; }
.adm .round:active{ transform:scale(.94); }
.adm .dhead b{ font-size:16px; font-weight:600; display:block; }
.adm .dhead .sub{ font-size:11.5px; color:var(--muted); }

.adm .body{ display:flex; flex-direction:column; gap:12px;
  padding:12px max(16px,env(safe-area-inset-right,0px)) 36px max(16px,env(safe-area-inset-left,0px)); }

.adm .toprow{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
.adm .status{ display:inline-flex; align-items:center; gap:7px; font-size:12.5px; color:var(--muted); }
.adm .dot{ width:8px; height:8px; border-radius:50%; background:var(--lime); box-shadow:0 0 0 4px rgba(182,245,66,.14); }

.adm .lead{ display:grid; grid-template-columns:auto 1fr; gap:12px; align-items:center; }
.adm .lead h1{ margin:0; font-size:20px; font-weight:700; letter-spacing:-.02em; }
.adm .lead p{ margin:2px 0 0; font-size:12.5px; color:var(--muted); }
.adm .lead-ico{ width:40px; height:40px; border-radius:12px; display:grid; place-items:center; background:linear-gradient(145deg,#23301a,#171a14); border:1px solid #2f3a22; color:var(--lime); }

.adm .card{ background:var(--card); border:1px solid var(--border); border-radius:var(--r-card); }

.adm .grid2{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.adm .metric{ display:grid; grid-template-columns:auto 1fr auto; column-gap:9px; align-items:center; padding:11px 12px; }
.adm .metric .mico{ width:28px; height:28px; border-radius:8px; display:grid; place-items:center; background:var(--card-2); color:var(--muted); border:1px solid var(--border); }
.adm .metric .mr{ min-width:0; }
.adm .metric .mtop{ display:flex; align-items:baseline; gap:6px; }
.adm .metric .val{ font-size:22px; font-weight:800; line-height:1; letter-spacing:-.02em; }
.adm .metric .lbl{ font-size:11px; color:var(--muted); margin-top:3px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.adm .sparkbox{ width:46px; height:32px; border-radius:8px; background:var(--card-2); border:1px solid var(--border); padding:4px; overflow:hidden; }
.adm .sparkbox svg{ display:block; width:100%; height:100%; }

.adm .section{ padding:13px; }
.adm .section>.head{ display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:11px; }
.adm .section .head .t{ display:inline-flex; align-items:center; gap:8px; font-size:14px; font-weight:600; }
.adm .section .head .t .hico{ color:var(--lime); display:inline-flex; }
.adm .link-r{ display:inline-flex; align-items:center; gap:2px; font-size:12.5px; color:var(--muted); background:none; border:0; cursor:pointer; font-family:inherit; padding:0; }
.adm .link-r:active{ color:var(--text); }

.adm .seg{ display:inline-flex; background:var(--card-2); border:1px solid var(--border); border-radius:999px; padding:3px; gap:2px; }
.adm .seg button{ font-family:inherit; font-size:11px; font-weight:600; color:var(--muted); background:none; border:0; padding:5px 10px; border-radius:999px; cursor:pointer; white-space:nowrap; }
.adm .seg button.on{ background:var(--lime); color:var(--lime-ink); }
.adm .legend{ display:flex; flex-wrap:wrap; gap:7px 14px; margin:8px 0 2px; font-size:11px; color:var(--muted); }
.adm .legend i{ width:9px; height:9px; border-radius:50%; display:inline-block; margin-right:6px; vertical-align:middle; }
.adm .chartwrap{ position:relative; }
.adm .chartwrap svg{ display:block; width:100%; height:auto; touch-action:pan-y; }
.adm .tip{ position:absolute; z-index:5; pointer-events:none; background:#0e1011; border:1px solid var(--border); border-radius:10px; padding:8px 10px; font-size:11.5px; min-width:120px; box-shadow:0 10px 30px -10px rgba(0,0,0,.7); transform:translate(-50%,-100%); }
.adm .tip .d{ color:var(--muted); margin-bottom:5px; font-weight:600; }
.adm .tip .tr{ display:flex; align-items:center; justify-content:space-between; gap:14px; }
.adm .tip .tr i{ width:8px; height:8px; border-radius:50%; display:inline-block; margin-right:6px; }

.adm .donutrow{ display:flex; align-items:center; gap:16px; }
.adm .donut{ position:relative; width:96px; height:96px; flex:0 0 96px; }
.adm .donut .center{ position:absolute; inset:0; display:grid; place-items:center; text-align:center; }
.adm .donut .center b{ font-size:23px; font-weight:800; }
.adm .srclist{ flex:1; display:flex; flex-direction:column; gap:12px; }
.adm .srcrow{ cursor:pointer; background:none; border:0; padding:0; font-family:inherit; color:inherit; text-align:left; width:100%; }
.adm .srcrow .l1{ display:flex; align-items:center; justify-content:space-between; font-size:13px; margin-bottom:6px; }
.adm .srcrow .l1 .nm{ display:inline-flex; align-items:center; gap:8px; }
.adm .srcrow .l1 .nm i{ width:9px; height:9px; border-radius:50%; display:inline-block; }
.adm .srcrow .l1 .pc{ font-weight:700; }
.adm .track{ height:6px; border-radius:999px; background:var(--card-2); overflow:hidden; }
.adm .track>span{ display:block; height:100%; border-radius:999px; }
.adm .cardbtn{ width:100%; margin-top:14px; font-family:inherit; font-size:13px; font-weight:600; color:var(--text); background:var(--card-2); border:1px solid var(--border); border-radius:12px; padding:11px; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:7px; }
.adm .cardbtn:active{ border-color:#3a4030; }
.adm .cardbtn .hico{ color:var(--lime); display:inline-flex; }

.adm .entry{ width:100%; font-family:inherit; display:flex; align-items:center; gap:13px; padding:14px; cursor:pointer; text-align:left; color:var(--text); }
.adm .entry:active{ border-color:#3a4030; }
.adm .entry .ei{ width:40px; height:40px; flex:0 0 40px; border-radius:11px; display:grid; place-items:center; background:var(--card-2); color:var(--lime); border:1px solid var(--border); }
.adm .entry .em{ flex:1; min-width:0; }
.adm .entry .em b{ font-size:14.5px; font-weight:600; display:block; }
.adm .entry .em span{ font-size:12px; color:var(--muted); }
.adm .entry .ec{ color:var(--muted); }

.adm .search{ position:relative; }
.adm .search svg{ position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--muted); }
.adm .search input{ width:100%; font-family:inherit; font-size:13.5px; color:var(--text); background:var(--card-2); border:1px solid var(--border); border-radius:12px; padding:11px 12px 11px 38px; outline:none; }
.adm .search input::placeholder{ color:var(--muted); }
.adm .search input:focus{ border-color:#3a4030; }
.adm .filters{ display:flex; gap:8px; overflow-x:auto; padding-bottom:2px; scrollbar-width:none; }
.adm .filters::-webkit-scrollbar{ display:none; }
.adm .chip{ flex:0 0 auto; font-family:inherit; font-size:12.5px; font-weight:500; color:var(--muted); background:var(--card-2); border:1px solid var(--border); border-radius:999px; padding:7px 14px; cursor:pointer; }
.adm .chip.on{ background:var(--text); color:#0b0d0e; border-color:var(--text); }
.adm .orglist{ display:flex; flex-direction:column; gap:10px; }
.adm .orgcard{ display:flex; align-items:center; gap:12px; padding:13px; cursor:pointer; width:100%; font-family:inherit; text-align:left; color:var(--text); }
.adm .orgcard:active{ border-color:#3a4030; }
.adm .ava{ width:44px; height:44px; flex:0 0 44px; border-radius:12px; display:grid; place-items:center; font-weight:700; font-size:18px; color:var(--text); background:linear-gradient(150deg,#262a24,#1a1c1a); border:1px solid var(--border); }
.adm .orgmid{ flex:1; min-width:0; display:block; }
.adm .orgmid .n1{ display:flex; align-items:center; gap:8px; }
.adm .orgmid .n1 b{ font-size:14.5px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.adm .orgmid .otype{ font-size:12px; color:var(--muted); margin-top:3px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; display:block; }
.adm .orgmid .otype .typ{ color:var(--text); }
.adm .orgmid .n2{ display:flex; align-items:center; gap:12px; font-size:11.5px; color:var(--muted); margin-top:5px; }
.adm .orgmid .n2 span{ display:inline-flex; align-items:center; gap:4px; }
.adm .orgright{ text-align:right; flex:0 0 auto; align-self:flex-start; }
.adm .orgright .st{ font-size:11.5px; display:inline-flex; align-items:center; gap:5px; white-space:nowrap; }
.adm .orgright .st i{ width:7px; height:7px; border-radius:50%; }
.adm .orgright .dt{ font-size:11px; color:var(--muted); margin-top:4px; }
.adm .badge{ font-size:10px; font-weight:700; padding:2px 7px; border-radius:6px; text-transform:uppercase; white-space:nowrap; }
.adm .b-free{ background:var(--card-2); color:var(--muted); border:1px solid var(--border); }
.adm .b-trial{ background:rgba(53,198,201,.14); color:var(--teal); border:1px solid rgba(53,198,201,.3); }
.adm .b-pro{ background:rgba(182,245,66,.16); color:var(--lime); border:1px solid rgba(182,245,66,.34); }
.adm .empty{ text-align:center; color:var(--muted); font-size:13px; padding:20px 0; }

.adm .events{ display:flex; flex-direction:column; }
.adm .ev{ display:flex; gap:12px; padding:11px 0; }
.adm .ev:not(:last-child){ border-bottom:1px solid rgba(43,45,46,.5); }
.adm .ev .evico{ width:30px; height:30px; flex:0 0 30px; border-radius:9px; display:grid; place-items:center; background:rgba(182,245,66,.12); color:var(--lime); border:1px solid rgba(182,245,66,.22); }
.adm .ev .evtx{ flex:1; min-width:0; }
.adm .ev .evtx b{ font-size:13.5px; font-weight:500; }
.adm .ev .evtx span{ display:block; font-size:11.5px; color:var(--muted); margin-top:2px; }

.adm .kpis{ display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }
.adm .kpi{ background:var(--card); border:1px solid var(--border); border-radius:14px; padding:12px; text-align:center; }
.adm .kpi .v{ font-size:22px; font-weight:800; }
.adm .kpi .l{ font-size:11px; color:var(--muted); margin-top:3px; }
.adm .rows{ display:flex; flex-direction:column; }
.adm .kv{ display:flex; align-items:center; justify-content:space-between; padding:12px 0; font-size:13.5px; width:100%; background:none; border:0; font-family:inherit; color:inherit; text-align:left; }
.adm .kv:not(:last-child){ border-bottom:1px solid rgba(43,45,46,.5); }
.adm .kv .k{ color:var(--muted); }
.adm .kv .v{ font-weight:600; }
.adm .funnel{ display:flex; flex-direction:column; gap:9px; }
.adm .fstep{ background:var(--card); border:1px solid var(--border); border-radius:14px; padding:12px 14px; display:flex; align-items:center; gap:13px; }
.adm .fstep .fi{ width:34px; height:34px; border-radius:10px; display:grid; place-items:center; background:var(--card-2); color:var(--lime); border:1px solid var(--border); }
.adm .fstep .fm{ flex:1; }
.adm .fstep .fm .fl{ font-size:12.5px; color:var(--muted); }
.adm .fstep .fm .fv{ font-size:20px; font-weight:800; }
.adm .conv{ font-size:11px; color:var(--muted); text-align:center; }
.adm .conv b{ color:var(--teal); font-weight:700; }
.adm .sectitle{ font-size:12px; font-weight:600; color:var(--muted); text-transform:uppercase; letter-spacing:.04em; margin:4px 2px 0; }

.adm .grantrow{ display:flex; flex-wrap:wrap; gap:8px; }
.adm .grantrow button{ flex:1 1 auto; min-width:72px; font-family:inherit; font-size:12.5px; font-weight:600; color:var(--lime); background:rgba(182,245,66,.1); border:1px solid rgba(182,245,66,.3); border-radius:10px; padding:9px 10px; cursor:pointer; }
.adm .grantrow button:active{ background:rgba(182,245,66,.2); }
.adm .grantrow button:disabled{ opacity:.5; cursor:default; }
.adm .grantrow button.danger{ color:#ff8a8a; background:rgba(255,138,138,.08); border-color:rgba(255,138,138,.3); }

.adm .gen{ padding:14px; }
.adm .gen .glab{ font-size:12.5px; color:var(--muted); margin-bottom:10px; }
.adm .geninput2{ width:100%; font-family:monospace; font-size:15px; color:var(--text); background:var(--card-2); border:1px solid var(--border); border-radius:12px; padding:12px; outline:none; }
.adm .geninput2::placeholder{ color:var(--muted); }
.adm .geninput2:focus{ border-color:#3a4030; }
.adm .genprev{ font-family:monospace; font-size:12px; color:var(--muted); margin-top:10px; word-break:break-all; }
.adm .genprev b{ color:var(--lime); font-weight:700; }
.adm .genbtn{ margin-top:12px; width:100%; font-family:inherit; font-size:13.5px; font-weight:700; color:var(--lime-ink); background:var(--lime); border:0; border-radius:12px; padding:12px; cursor:pointer; }
.adm .genbtn:disabled{ opacity:.45; cursor:default; }
.adm .generr{ font-size:11.5px; color:#ff8a8a; margin-top:8px; min-height:14px; }
.adm .lbllist{ display:flex; flex-direction:column; gap:9px; margin-top:4px; }
.adm .lblrow{ display:flex; align-items:center; gap:11px; background:var(--card); border:1px solid var(--border); border-radius:12px; padding:11px 12px; }
.adm .lblrow .lb{ flex:1; min-width:0; }
.adm .lblrow .lb b{ font-size:13px; font-weight:600; display:flex; align-items:center; gap:7px; }
.adm .lblrow .lb .ln{ font-family:monospace; font-size:11px; color:var(--muted); margin-top:3px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.adm .lblrow .cnt{ text-align:right; flex:0 0 auto; }
.adm .lblrow .cnt b{ font-size:15px; font-weight:800; }
.adm .lblrow .cnt span{ font-size:10px; color:var(--muted); display:block; }
.adm .copy{ flex:0 0 34px; width:34px; height:34px; border-radius:9px; display:grid; place-items:center; background:var(--card-2); border:1px solid var(--border); color:var(--muted); cursor:pointer; padding:0; }
.adm .copy:active{ color:var(--lime); border-color:#3a4030; }
.adm .copy:disabled{ opacity:.5; cursor:default; }
.adm .copy.danger:active{ color:#ff8a8a; }

.adm .toast{ position:fixed; left:50%; bottom:26px; transform:translateX(-50%) translateY(10px); background:var(--text); color:#0b0d0e; font-size:12.5px; font-weight:600; padding:9px 16px; border-radius:999px; opacity:0; transition:.18s; z-index:50; pointer-events:none; }
.adm .toast.on{ opacity:1; transform:translateX(-50%) translateY(0); }
`;
